import React, { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../Components/Header.tsx";
import Sidebar from "../Components/Sidebar.tsx";
import { ExportProvider } from "../Context/ExportContext.tsx";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { ProgressSpinner } from "primereact/progressspinner";
import { Image } from "primereact/image";
import { Toast } from "primereact/toast";
import ICONS from "../Constants/Icons.tsx";
import { useTheme } from "../ThemeContext/ThemeContext.tsx";
import useAccessToken from "../Custom/useAccessToken.tsx";
import { RootState } from "../Redux/Store.tsx";
import "../Components/EngagementLayout.css";
import { Skeleton } from "primereact/skeleton";

// Regex to extract engagementId from engagement route paths
const ENGAGEMENT_ROUTE_RE =
  /\/(overview|workflow|budget|monitor|requests|timeentries|expenses)\/([^/?#]+)/;
const THINK_PANEL_OPEN_KEY = "think_panel_open";
const THINK_IFRAME_SRC_KEY = "think_iframe_src";

const Layout = () => {
  const suppressIframeReloadRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const { thinkAccessToken } = useAccessToken();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const popupOpenedRef = useRef(false);
  const toast = useRef<Toast>(null);
  // Track previous context to detect engagement changes and insert dividers
  const prevContextRef = useRef<{
    engagementId: string | null;
    pageContextLabel: string;
  }>({ engagementId: null, pageContextLabel: "" });
  // Banner shown in the Think panel when engagement context changes
  const [contextChangeBanner, setContextChangeBanner] = useState<string | null>(
    null,
  );

  // Restore Think panel state from sessionStorage so a browser refresh doesn't lose it
  const [showThinkPanel, setShowThinkPanel] = useState(
    () => sessionStorage.getItem(THINK_PANEL_OPEN_KEY) === "true",
  );
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const [authErrorDetected, setAuthErrorDetected] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Stable iframe URL — set once when panel opens, only updated explicitly (new chat / theme)
  // Restored from sessionStorage on browser refresh so the same conversation reloads.
  // Always patch the theme param to match the current Harmonize theme on restore.
  const [iframeSrc, setIframeSrc] = useState(() => {
    const saved = sessionStorage.getItem(THINK_IFRAME_SRC_KEY) || "";
    if (!saved) return "";
    try {
      const url = new URL(saved);
      url.searchParams.set("theme", isDarkMode ? "dark" : "light");
      return url.toString();
    } catch {
      return saved;
    }
  });

  // Read engagement context from Redux + current URL
  const engagementTask = useSelector(
    (state: RootState) => state.TaskBudget.engagementTask,
  );
  const engagementMatch = ENGAGEMENT_ROUTE_RE.exec(location.pathname);
  const engagementId = engagementMatch ? engagementMatch[2] : null;
  const engagementName = (engagementTask?.EngagementName ?? "")
    .toString()
    .trim();
  const engagementNumber = (engagementTask?.EngagementNumber ?? "")
    .toString()
    .trim();
  const isEngagementRoute = Boolean(engagementId);
  const engagementLabel =
    engagementName && engagementNumber
      ? `${engagementName} (${engagementNumber})`
      : engagementName || engagementNumber || engagementId || "";
  const pageContextLabel = (() => {
    const path = location.pathname || "/";
    if (path === "/engagements") return "Active Engagements";
    if (path === "/requests") return "Requests";
    if (path.startsWith("/viewrequests")) {
      try {
        const breadcrumbRaw = localStorage.getItem("lastRequestBreadcrumb");
        if (breadcrumbRaw) {
          const breadcrumbObj = JSON.parse(breadcrumbRaw);
          if (breadcrumbObj && Array.isArray(breadcrumbObj.breadcrumb)) {
            return breadcrumbObj.breadcrumb.map((b) => b.label).filter(Boolean).join(" | ");
          } else if (breadcrumbObj && breadcrumbObj.requestTypeName) {
            return breadcrumbObj.requestTypeName;
          }
        }
      } catch (e) {
      }
      return "Requests";
    }
    if (path === "/time-report") return "Time Report";
    if (path === "/admin" || path.startsWith("/admin/")) return "Admin";
    if (path === "/" || path.startsWith("/dashboard")) return "Dashboard";
    return "Dashboard";
  })();

  // Time report context from the IndividualTimeReport page
  const [timeReportContext, setTimeReportContext] = useState<{
    fromDate: string;
    toDate: string;
    staffIds: string[];
  } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) setTimeReportContext(detail);
    };
    window.addEventListener("time-report-context-update", handler);
    return () =>
      window.removeEventListener("time-report-context-update", handler);
  }, []);

  // Clear time report context when navigating away from /time-report
  useEffect(() => {
    if (location.pathname !== "/time-report") {
      setTimeReportContext(null);
    }
  }, [location.pathname]);

  // Dynamic page-specific details from child pages.
  const [pageDetailsExtras, setPageDetailsExtras] = useState<Record<
    string,
    unknown
  > | null>(null);
  const timeReportPageDetails =
    location.pathname === "/time-report" && timeReportContext
      ? {
          staffIds: timeReportContext.staffIds,
          fromDate: timeReportContext.fromDate,
          toDate: timeReportContext.toDate,
        }
      : null;

  const pageDetails = {
    pageName:
      location.pathname === "/" || location.pathname.startsWith("/dashboard")
        ? "dashboard"
        : location.pathname === "/engagements"
          ? "active_engagements"
          : location.pathname === "/requests"
            ? "requests"
            : location.pathname.startsWith("/viewrequests")
              ? "view_requests"
              : location.pathname === "/time-report"
                ? "time_report"
                : location.pathname === "/admin" ||
                    location.pathname.startsWith("/admin/")
                  ? "admin"
                  : isEngagementRoute
                    ? "engagement"
                    : "dashboard",
    pathName: location.pathname,
    pageContextLabel,
    ...(timeReportPageDetails || {}),
    ...(pageDetailsExtras || {}),
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === "object") {
        setPageDetailsExtras(detail as Record<string, unknown>);
      }
    };
    window.addEventListener("page-details-update", handler);
    return () => window.removeEventListener("page-details-update", handler);
  }, []);

  // Clear page-specific extras when route changes; base pageDetails are derived above.
  useEffect(() => {
    setPageDetailsExtras(null);
  }, [location.pathname]);

  // Use a per-engagement key so conversations are scoped (same as EngagementLayout used to do)
  const conversationKey = engagementId
    ? `think_conversation_${engagementId}`
    : "think_global_conversation";

  // Resolve thinkHost safely (handles unreplaced {{THINK_URL}} placeholders)
  const thinkHost = (() => {
    const raw = (process.env.REACT_APP_THINK_URL ?? "")
      .trim()
      .replace(/\/+$/, "");
    try {
      new URL(raw);
      return raw;
    } catch {
      return "https://think.lbmc.app";
    }
  })();
  const thinkSkillSlug =
    (process.env.REACT_APP_THINK_SKILL ?? "").trim() || "harmonize-skill";
  const thinkOrigin = (() => {
    try {
      return new URL(thinkHost).origin;
    } catch {
      return "https://think.lbmc.app";
    }
  })();

  const getEmbedUrl = useCallback(
    (forceNew = false) => {
      const host = thinkHost.replace(/\/$/, "");
      const theme = isDarkMode ? "dark" : "light";
      const saved =
        !forceNew && (conversationId || localStorage.getItem(conversationKey));
      const params = new URLSearchParams({
        embedding: "true",
        theme,
      });

      if (saved) {
        return `${host}/app/${thinkSkillSlug}/${saved}?${params.toString()}`;
      }

      return `${host}/app/${thinkSkillSlug}?${params.toString()}`;
    },
    [thinkHost, isDarkMode, conversationId, conversationKey, thinkSkillSlug],
  );

  // Load saved conversation when the conversation key changes (e.g. navigating between engagements)
  useEffect(() => {
    const saved = localStorage.getItem(conversationKey);
    setConversationId(saved);
  }, [conversationKey]);

  // Persist panel open/close state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      THINK_PANEL_OPEN_KEY,
      showThinkPanel ? "true" : "false",
    );
  }, [showThinkPanel]);

  // Persist iframe src to sessionStorage whenever it changes
  useEffect(() => {
    if (iframeSrc) {
      sessionStorage.setItem(THINK_IFRAME_SRC_KEY, iframeSrc);
    }
  }, [iframeSrc]);

  // When the panel opens for the first time, set the URL once and never touch it again
  // on subsequent route changes — this keeps the iframe alive across navigation.
  useEffect(() => {
    if (showThinkPanel && !iframeSrc) {
      setIframeSrc(getEmbedUrl());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showThinkPanel]);

  // Close the Think panel and clear all conversation state so the next
  // open always starts a fresh new chat (no stale history).
  const closeThinkPanel = useCallback(() => {
    setShowThinkPanel(false);
    setIframeSrc("");
    setConversationId(null);
    sessionStorage.removeItem(THINK_IFRAME_SRC_KEY);
    localStorage.removeItem(conversationKey);
  }, [conversationKey]);

  // Open panel when header button fires event — works on ALL routes
  useEffect(() => {
    const handler = () => setShowThinkPanel(true);
    globalThis.addEventListener("open-think-sidebar", handler);
    return () => globalThis.removeEventListener("open-think-sidebar", handler);
  }, []);

  // Clear Think state_history when time-report Apply is clicked
  // Sends a postMessage so the iframe clears history without any visual reload
  useEffect(() => {
    const handler = () => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "THINK_CLEAR_HISTORY" },
          thinkOrigin,
        );
      } catch {}
    };
    globalThis.addEventListener("think-clear-history", handler);
    return () => globalThis.removeEventListener("think-clear-history", handler);
  }, [thinkOrigin]);

  // Close panel when other parts of the app request it
  useEffect(() => {
    const handler = () => setShowThinkPanel(false);
    globalThis.addEventListener("close-think-sidebar", handler);
    return () => globalThis.removeEventListener("close-think-sidebar", handler);
  }, []);

  // Think panel is not available on Overview, Workflow, or Workflow 2 — close it on navigation
  useEffect(() => {
    const match = ENGAGEMENT_ROUTE_RE.exec(location.pathname);
    const tab = match?.[1];
    if (tab === "overview" || tab === "workflow") {
      setShowThinkPanel(false);
    }
  }, [location.pathname]);

  // On Harmonize logout: close the Think panel and clear all conversation history
  useEffect(() => {
    const handler = () => {
      closeThinkPanel();
      // Clear all think conversation keys (covers all engagements)
      Object.keys(localStorage)
        .filter(
          (k) =>
            k.startsWith("think_conversation_") ||
            k === "think_global_conversation",
        )
        .forEach((k) => localStorage.removeItem(k));
      sessionStorage.removeItem(THINK_PANEL_OPEN_KEY);
    };
    globalThis.addEventListener("harmonize-logout", handler);
    return () => globalThis.removeEventListener("harmonize-logout", handler);
  }, [closeThinkPanel]);

  // Reset loading/error state each time the panel opens/closes
  useEffect(() => {
    if (!showThinkPanel) {
      setIframeLoading(true);
      setIframeError(false);
      setAuthErrorDetected(false);
      return;
    }
    const timeout = setTimeout(() => {
      if (iframeLoading) setIframeLoading(false);
    }, 8000);
    return () => clearTimeout(timeout);
  }, [showThinkPanel, iframeLoading]);

  // Auto-open popup when auth error is detected
  useEffect(() => {
    if (authErrorDetected && showThinkPanel && !popupOpenedRef.current) {
      popupOpenedRef.current = true;
      openThinkPopup();
    }
  }, [authErrorDetected, showThinkPanel]);

  // Sync theme changes live to the iframe via postMessage (no reload needed)
  useEffect(() => {
    if (!iframeRef.current?.contentWindow) return;
    try {
      iframeRef.current.contentWindow.postMessage(
        {
          type: "THINK_THEME",
          theme: isDarkMode ? "dark" : "light",
          isDarkMode,
        },
        thinkOrigin,
      );
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDarkMode]);

  // Post context to the iframe for both engagement and non-engagement routes.
  const postThinkContext = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;

    const clientName = (engagementTask?.ClientName ?? "").toString().trim();

    const isTimeReportRoute = location.pathname === "/time-report";

    const payload: Record<string, unknown> = {
      type: "ENGAGEMENT_CONTEXT",
      engagementId: isEngagementRoute ? engagementId : null,
      engagementName: isEngagementRoute ? engagementName || null : null,
      engagementNumber: isEngagementRoute ? engagementNumber || null : null,
      engagementLabel: isEngagementRoute ? engagementLabel || null : null,
      clientName: isEngagementRoute ? clientName || null : null,
      contextType: isEngagementRoute ? "engagement" : "context",
      contextPrefix: isEngagementRoute ? "Engagement" : "Context",
      contextLabel: isEngagementRoute
        ? engagementLabel || null
        : pageContextLabel,
      pageContextLabel: isEngagementRoute ? null : pageContextLabel,
      // UI hints for the Think app
      contextClickable: false,
      contextDisplay: isEngagementRoute
        ? {
            clientName: clientName || null,
            engagementName: engagementName || null,
            engagementNumber: engagementNumber || null,
            multiline: true,
          }
        : null,
    };

    // Attach time report context when on the time-report page, clear it otherwise
    if (isTimeReportRoute && timeReportContext) {
      payload.timeReportContext = timeReportContext;
    } else {
      payload.timeReportContext = null;
    }

    payload.pageDetails = pageDetails;
    try {
      iframeRef.current.contentWindow.postMessage(payload, thinkOrigin);
      iframeRef.current.contentWindow.postMessage(
        { ...payload, type: "THINK_ENGAGEMENT_CONTEXT" },
        thinkOrigin,
      );
    } catch {}
  }, [
    isEngagementRoute,
    engagementId,
    engagementName,
    engagementNumber,
    engagementLabel,
    pageContextLabel,
    thinkOrigin,
    engagementTask,
    location.pathname,
    timeReportContext,
    pageDetails,
  ]);



  // Receive messages from the iframe
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== thinkOrigin) return;
      if (
        event.data?.type === "THINK_AUTH_ERROR" ||
        event.data?.error === "redirect_in_iframe"
      ) {
        setAuthErrorDetected(true);
        setIframeError(true);
        setIframeLoading(false);
      }
      if (
        event.data?.type === "THINK_CONVERSATION_ID" &&
        event.data?.conversationId
      ) {
        const cid = event.data.conversationId;
        setConversationId(cid);
        localStorage.setItem(conversationKey, cid);
        // Keep sessionStorage iframe URL in sync so a page refresh loads
        // the current conversation instead of a stale one (e.g. after the
        // user clicks "New Chat" inside the Think iframe).
        const theme = isDarkMode ? "dark" : "light";
        const params = new URLSearchParams({ embedding: "true", theme });
        const updatedUrl = `${thinkHost.replace(/\/$/, "")}/app/${thinkSkillSlug}/${cid}?${params.toString()}`;
        sessionStorage.setItem(THINK_IFRAME_SRC_KEY, updatedUrl);
      }
      if (event.data?.type === "THINK_REQUEST_ENGAGEMENT_CONTEXT") {
        postThinkContext();
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    thinkOrigin,
    conversationKey,
    postThinkContext,
    isDarkMode,
    thinkHost,
    thinkSkillSlug,
  ]);

  // Handle engagement context change: reload iframe with new conversation and show banner.
  const handleContextChange = useCallback(
    (
      prev: { engagementId: string | null; pageContextLabel: string },
      currentContextId: string | null,
    ) => {
      const newLabel = isEngagementRoute
        ? engagementLabel || engagementId || "new engagement"
        : pageContextLabel || "new page";
      // setContextChangeBanner(`Context changed to ${newLabel}`);

      // Suppress iframe reload if navigation was triggered by Think iframe
      if (suppressIframeReloadRef.current) {
        suppressIframeReloadRef.current = false;
        return;
      }
      // Reload iframe with the correct conversation for the new engagement
      setIframeLoading(true);
      const newUrl = getEmbedUrl();
      setIframeSrc(newUrl);
      if (iframeRef.current) iframeRef.current.src = newUrl;

      // Send CONTEXT_DIVIDER to the iframe (in case the Think app supports it)
      if (iframeRef.current?.contentWindow) {
        try {
          iframeRef.current.contentWindow.postMessage(
            {
              type: "CONTEXT_DIVIDER",
              previousContext: {
                engagementId: prev.engagementId,
                pageContextLabel: prev.pageContextLabel,
              },
              newContext: {
                engagementId: currentContextId,
                engagementLabel: isEngagementRoute
                  ? engagementLabel || null
                  : null,
                pageContextLabel: isEngagementRoute ? null : pageContextLabel,
              },
              timestamp: new Date().toISOString(),
            },
            thinkOrigin,
          );
        } catch {}
      }
    },
    [
      isEngagementRoute,
      engagementId,
      engagementLabel,
      pageContextLabel,
      thinkOrigin,
      getEmbedUrl,
    ],
  );

  // When the engagement context changes while the Think panel is open,
  // reload the iframe with the new engagement's conversation so only that
  // engagement's history is sent as AI context, and show a visual banner.
  useEffect(() => {
    if (!showThinkPanel || iframeLoading || iframeError) return;

    const prev = prevContextRef.current;
    const currentContextId = isEngagementRoute ? engagementId : null;
    const currentPageLabel = isEngagementRoute ? "" : pageContextLabel;
    const currentContextType = isEngagementRoute ? "engagement" : "global";
    const prevContextType = prev.engagementId ? "engagement" : "global";

    const isInitialMount =
      prev.engagementId === null && prev.pageContextLabel === "";
    const engagementChanged = prev.engagementId !== currentContextId;
    const contextTypeChanged = prevContextType !== currentContextType;
    const pageContextChanged = prev.pageContextLabel !== currentPageLabel;

    // Only reload if switching between two different engagementIds
    if (
      !isInitialMount &&
      prevContextType === "engagement" &&
      currentContextType === "engagement" &&
      engagementChanged
    ) {
      handleContextChange(prev, currentContextId);
      const timer = setTimeout(() => setContextChangeBanner(null), 5000);
      prevContextRef.current = {
        engagementId: currentContextId,
        pageContextLabel: currentPageLabel,
      };
      return () => clearTimeout(timer);
    }

    // For all other context switches, just send context
    if (!isInitialMount && (contextTypeChanged || pageContextChanged)) {
      postThinkContext();
      prevContextRef.current = {
        engagementId: currentContextId,
        pageContextLabel: currentPageLabel,
      };
      return;
    }

    prevContextRef.current = {
      engagementId: currentContextId,
      pageContextLabel: currentPageLabel,
    };
    postThinkContext();
  }, [
    engagementId,
    engagementName,
    engagementNumber,
    pageContextLabel,
    showThinkPanel,
    iframeLoading,
    iframeError,
    postThinkContext,
    isEngagementRoute,
    engagementLabel,
    handleContextChange,
    timeReportContext,
    pageDetails,
  ]);

  const handleIframeLoad = () => {
    if (iframeRef.current?.contentWindow && thinkAccessToken) {
      try {
        iframeRef.current.contentWindow.postMessage(
          { type: "AUTH_TOKEN", token: thinkAccessToken },
          thinkOrigin,
        );
      } catch {}
    }
    try {
      iframeRef.current?.contentWindow?.postMessage(
        {
          type: "THINK_THEME",
          theme: isDarkMode ? "dark" : "light",
          isDarkMode,
        },
        thinkOrigin,
      );
    } catch {}
    postThinkContext();
    setIframeLoading(false);
  };

  const openThinkPopup = () => {
    const url = getEmbedUrl();
    const width = 1200;
    const height = 800;
    const left = Math.max(0, (window.screen.width - width) / 2);
    const top = Math.max(0, (window.screen.height - height) / 2);
    const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes,toolbar=no,menubar=no`;
    const win = window.open(url, "HarmonizeSkill", features);
    if (win) {
      win.focus();
      closeThinkPanel();
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Popup Blocked",
        detail: "Please allow popups to open Harmonize Skill",
        life: 5000,
      });
    }
  };

  const startNewChat = () => {
    localStorage.removeItem(conversationKey);
    setConversationId(null);
    setIframeLoading(true);
    const newUrl = getEmbedUrl(true);
    setIframeSrc(newUrl);
    if (iframeRef.current) iframeRef.current.src = newUrl;
  };

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "OPEN_ENGAGEMENT") {
        const { engagementId } = event.data;

        if (!engagementId) return;

        suppressIframeReloadRef.current = true;

        // ✅ Prevent same-route navigation (VERY IMPORTANT)
        if (location.pathname !== `/overview/${engagementId}`) {
          navigate(`/overview/${engagementId}`);
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [navigate, location.pathname]);

  return (
    <ExportProvider>
      <div className="dashboard">
        <Toast ref={toast} />
        <Header />
        <Sidebar />
        {/* layout-row reuses the same split classes as EngagementLayout */}
        <div
          className={`layout-row global-layout-row ${showThinkPanel ? "split" : ""}`}
        >
          <div className="main-content">
            <Outlet />
          </div>

          {showThinkPanel && (
            <div className="think-panel">
              <Button
                icon="pi pi-times"
                className="p-button-text close-button"
                onClick={closeThinkPanel}
                aria-label="Close panel"
                tooltip="Close"
                tooltipOptions={{ position: "left" }}
              />

              {/* Context-change divider banner */}
              {contextChangeBanner && (
                <div className="context-change-banner">
                  <div className="context-change-line" />
                  <span className="context-change-label">
                    {contextChangeBanner}
                  </span>
                  <div className="context-change-line" />
                </div>
              )}

              <div className="think-sidebar-content">
                {iframeLoading && !iframeError && (
                  <div className="think-sidebar-loading"></div>
                )}

                {iframeError && (
                  <div className="think-sidebar-error">
                    <div className="think-sidebar-error-title">
                      <i
                        className="pi pi-exclamation-triangle"
                        style={{ marginRight: "8px" }}
                      />
                      {authErrorDetected
                        ? "Authentication Issue Detected"
                        : "Unable to Load"}
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                      {authErrorDetected
                        ? "The embedded view cannot authenticate properly. Please use the popup window instead."
                        : "There was a problem loading the Harmonize Skill in embedded mode."}
                    </div>
                    <Button
                      label="Open in Popup Window"
                      icon="pi pi-external-link"
                      onClick={openThinkPopup}
                      severity="info"
                      size="small"
                    />
                  </div>
                )}

                {!iframeError && !authErrorDetected && (
                  <iframe
                    ref={iframeRef}
                    src={iframeSrc}
                    className="think-embedded-iframe"
                    title="Think Harmonize Skill"
                    allow="clipboard-read; clipboard-write"
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                    onLoad={handleIframeLoad}
                    onError={() => {
                      setIframeError(true);
                      setIframeLoading(false);
                    }}
                    style={{ display: iframeLoading ? "none" : "block" }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ExportProvider>
  );
};

export default Layout;